/**
 * Utility functions for image URL processing
 */

/**
 * Repairs image URLs that were stored with a duplicated prefix due to a
 * server-side bug (e.g. Target's
 * `https://target.scene7.com/is/image/Target/https://target.scene7.com/is/image/Target/GUEST_...`).
 * If a second absolute URL is found embedded in the path, returns just the
 * inner URL. Handles both `https://` and protocol-relative `//` outer URLs.
 */
function repairDoubledImageUrl(imageUrl: string): string {
  const match = imageUrl.match(/^(?:https?:)?\/\/[^/]+\/.*?(https?:\/\/.+)$/i);
  return match ? match[1] : imageUrl;
}

/**
 * Processes an image URL by replacing the <SIZE> placeholder with the specified size
 * @param imageUrl - The image URL that may contain <SIZE> placeholder
 * @param size - The size to replace <SIZE> with (default: "600")
 * @returns The processed image URL with size replaced
 */
export function processImageUrl(
  imageUrl: string | undefined,
  size: string = "600"
): string {
  if (!imageUrl) return "";

  return repairDoubledImageUrl(imageUrl).replace("<SIZE>", size);
}

/**
 * Asks the retailer's image CDN for a photo about `size` px instead of the full-size original.
 * Measured on 2026-10-23 for a ~200px card: Walmart 382 KB → 33 KB, Amazon 75 KB → 11 KB,
 * Lowe's 56 KB → 10 KB, Best Buy PNGs 793 KB → 16 KB. URLs that already carry size settings,
 * and hosts not listed here, are left alone.
 */
export function sizeForImageCdn(imageUrl: string, size: string): string {
  const px = Number.parseInt(size, 10);
  if (!imageUrl || !Number.isFinite(px) || px <= 0) return imageUrl;
  let url: URL;
  try {
    url = new URL(imageUrl);
  } catch {
    return imageUrl;
  }
  const host = url.hostname;
  // Amazon: the size lives in the file name (._AC_SL1500. → ._AC_SL400.).
  if (/(^|\.)media-amazon\.com$/.test(host)) {
    return imageUrl.replace(/\._AC_S[LXY]\d+_?\./, `._AC_SL${px}.`);
  }
  if (url.search) return imageUrl;
  if (/(^|\.)walmartimages\.com$/.test(host)) return `${imageUrl}?odnHeight=${px}&odnWidth=${px}&odnBg=FFFFFF`;
  if (/(^|\.)scene7\.com$/.test(host)) return `${imageUrl}?wid=${px}&hei=${px}&qlt=80`;
  // Lowe's mobile CDN has fixed renditions; "pdhi" is 675px, plenty for any card or detail view.
  if (host === "mobileimages.lowes.com" && px <= 675) return `${imageUrl}?size=pdhi`;
  // Best Buy takes ;key=value options on the path.
  if (host === "pisces.bbystatic.com" && !imageUrl.includes(";")) return `${imageUrl};maxHeight=${px};maxWidth=${px}`;
  return imageUrl;
}

/**
 * Processes an image URL for display in product components
 * @param imageUrl - The image URL that may contain <SIZE> placeholder
 * @param size - The size to replace <SIZE> with (default: "600")
 * @returns The processed image URL with size replaced
 */
export function getProductImageUrl(
  imageUrl: string | undefined,
  size: string = "600"
): string {
  return sizeForImageCdn(processImageUrl(imageUrl, size), size);
}
